import React, { useEffect, useState, forwardRef } from "react";
import { Chart, ChartCanvas } from "@react-financial-charts/core";
import { XAxis, YAxis } from "@react-financial-charts/axes";
import { format } from "d3-format";
import { rsi } from "@react-financial-charts/indicators";
import { discontinuousTimeScaleProviderBuilder } from "@react-financial-charts/scales";
import { lastVisibleItemBasedZoomAnchor } from "react-financial-charts";
import {
  RSISeries,
  AlternatingFillAreaSeries,
} from "@react-financial-charts/series";
import { SingleValueTooltip } from "@react-financial-charts/tooltip";
import { withDeviceRatio, withSize } from "@react-financial-charts/utils";
import { nFormatter } from "../../../utils/utils";

// TODO:- We can get Middle Top(overBought)  Bottom(OverSold) throught Props But For Now I've HardCoded here
const OVER_BOUGHT = 70;
const MIDDLE = 50;
const OVER_SOLD = 30;


const RSIIndicator = forwardRef(({
  height,
  ratio,
  width,
  initialData,
  xAccessor,
  displayXAccessor,
  xScale,
  xExtents,
  showXAxis = true,
  windowSize = 14,
  margin,
  padding,
}, ref) => {
  const [calculatedData, setCalculatedData] = useState([]);
  const xScaleProvider =
    discontinuousTimeScaleProviderBuilder().inputDateAccessor((d) => d.date);

  // Compute RSI from close prices using standard Wilder's smoothing method.
  // sourcePath: "close" reads d.close from each bar.
  // The library stores the result at d.rsi (lowercase).
  const rsiCalculator = rsi()
    .options({ windowSize })
    .accessor((d) => d.rsi);

  useEffect(() => {
    const formatedData = initialData.map((item) => ({
      ...item,
      date: new Date(item.timestamp || item.date),
    }));
    const calculatedData = rsiCalculator(formatedData);
    setCalculatedData(calculatedData);
  }, [initialData, windowSize]);

  const { data } = xScaleProvider(calculatedData);
  const yAccessor = rsiCalculator.accessor();

  const calculateOptimalTicks = (data, chartHeight) => {
    const validRSI = data.filter((item) => item.rsi != null).map((item) => item.rsi);
    if (validRSI.length === 0) return 3;
    const dataRange = Math.max(...validRSI) - Math.min(...validRSI);
    let baseTickNumber = Math.floor(chartHeight / 100);
    const rangeAdjustment = Math.ceil(dataRange / 10);
    const finalTickNumber = baseTickNumber + rangeAdjustment;
    return Math.max(finalTickNumber, 3);
  };
  const optimalTicks = calculateOptimalTicks(calculatedData, height);

  return (
    <ChartCanvas
      ref={ref}
      height={height}
      ratio={ratio}
      width={width}
      data={data}
      displayXAccessor={displayXAccessor}
      seriesName="Data"
      xScale={xScale}
      xAccessor={xAccessor}
      xExtents={xExtents}
      margin={margin}
      padding={padding}
      disableZoom={false}
      disablePan={false}
      disableInteraction={false}
      clamp={false}
      zoomAnchor={lastVisibleItemBasedZoomAnchor}
    >
      {/* Client: "When you have two indicators, the top of the top indicator and
          the bottom of the bottom indicator are cut off. You have to scroll out
          on the chart to see the RSI under 30 and the ADL at its current level.
          Can we add some padding without messing up these indicators staying
          connected to price when a user scrolls in or out?"

          `padding` on <Chart> is exactly that guarantee. It shrinks the y RANGE
          by 8px at each end — the plot is drawn 16px shorter inside the same
          panel — and leaves the y DOMAIN, the x scale and xExtents untouched.
          Horizontal sync with the price chart is driven entirely by the shared
          xScale/xExtents, so panning and zooming stay locked to price exactly as
          before; only the vertical breathing room changes. */}
      <Chart id={1} yExtents={yAccessor} padding={{ top: 8, bottom: 8 }}>
        {showXAxis && <XAxis strokeStyle={"#e5e7eb"}
          tickStrokeStyle={"#e5e7eb"} />}
        {!showXAxis && (
          <XAxis
            strokeStyle={"#e5e7eb"}
            tickStrokeStyle={"#e5e7eb"}
            showTicks={false}
            showTickLabel={false}
            xZoomHeight={1}
            tickPadding={0}
          />
        )}
        {/* <YAxis tickFormat={format(".2f")} ticks={5}/> */}
        <YAxis strokeStyle={"#e5e7eb"}
          tickStrokeStyle={"#e5e7eb"} tickFormat={format(".2f")} ticks={optimalTicks} />
        <XAxis
          strokeStyle={"#e5e7eb"}
          tickStrokeStyle={"#e5e7eb"}
          showTicks={false}
          xZoomHeight={1}
          tickPadding={0}
          axisAt={"top"}
          orient={"top"}
        />
        <YAxis
          strokeStyle={"#e5e7eb"}
          tickStrokeStyle={"#e5e7eb"}
          showTicks={false}
          yZoomWidth={1}
          tickPadding={0}
          axisAt={"left"}
          orient={"left"}
        />

        <RSISeries
          yAccessor={yAccessor}
          strokeStyle={{
            insideThreshold: "#a87878",
            outsideThreshold: "#779a74",
            top: "#555555",
            bottom: "#555555",
            middle: "#7f7f7f",
          }}
          overBought={OVER_BOUGHT}
          overSold={OVER_SOLD}
          middle={MIDDLE}
          strokeDasharray={{ middle: "Dash" }}
        />

        <AlternatingFillAreaSeries
          yAccessor={yAccessor}
          baseAt={OVER_BOUGHT}
          fillStyle={{ top: "#779a74", bottom: "transparent" }}
          strokeStyle={{ top: "#24422b", bottom: "#24422b" }}
        />

        <AlternatingFillAreaSeries
          yAccessor={yAccessor}
          baseAt={OVER_SOLD}
          fillStyle={{ top: "transparent", bottom: "#b07673" }}
          strokeStyle={{ top: "#555555", bottom: "#5a4848" }}
        />
      </Chart>
    </ChartCanvas>
  );
});

export default RSIIndicator;
